import { z } from 'zod';

export const MonitoringHourTypeSchema = z.enum(['MONITORING', 'CHECK_IN']);
export type MonitoringHourType = z.infer<typeof MonitoringHourTypeSchema>;

// ============================================================================
// PARAM SCHEMAS (namespaced to avoid collisions)
// ============================================================================

export const monitoringHoursRuleIdParam = z.object({ id: z.string().min(1) });
export type MonitoringHoursRuleIdParam = z.infer<typeof monitoringHoursRuleIdParam>;

export const monitoringHoursEventIdParam = z.object({ id: z.string().min(1) });
export type MonitoringHoursEventIdParam = z.infer<typeof monitoringHoursEventIdParam>;

// ============================================================================
// RULES
// ============================================================================

export const listMonitoringHoursRulesQuery = z.object({
  patientId: z.uuid(),
});
export type ListMonitoringHoursRulesQuery = z.infer<typeof listMonitoringHoursRulesQuery>;

export const deleteMonitoringHoursRuleQuery = z.object({
  start: z.string().optional(),
});
export type DeleteMonitoringHoursRuleQuery = z.infer<typeof deleteMonitoringHoursRuleQuery>;

export const createMonitoringHoursRuleBody = z
  .object({
    start: z.iso.datetime({ offset: true }),
    end: z.iso.datetime({ offset: true }),
    repeats: z.boolean(),
    freq: z.number().optional(),
    patientId: z.uuid(),
    interval: z.number().optional(),
    byDay: z.array(z.number()).optional(),
    until: z.iso.datetime({ offset: true }).optional(),
    type: MonitoringHourTypeSchema,
    checkInInterval: z.number().optional(),
    checkInDuration: z.number().optional(),
  })
  .refine(
    (v) => v.type !== 'CHECK_IN' || (typeof v.checkInInterval === 'number' && typeof v.checkInDuration === 'number'),
    { message: 'checkInInterval and checkInDuration are required when type is CHECK_IN' },
  );
export type CreateMonitoringHoursRuleBody = z.infer<typeof createMonitoringHoursRuleBody>;

export const updateMonitoringHoursRuleBody = z.object({
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  repeats: z.boolean(),
  freq: z.number().optional(),
  interval: z.number().optional(),
  byDay: z.array(z.number()).optional(),
  until: z.iso.datetime({ offset: true }).optional(),
  type: MonitoringHourTypeSchema,
  checkInInterval: z.number().optional(),
  checkInDuration: z.number().optional(),
});
export type UpdateMonitoringHoursRuleBody = z.infer<typeof updateMonitoringHoursRuleBody>;

// ============================================================================
// EVENTS
// ============================================================================

export const listMonitoringHoursEventsQuery = z.object({
  patientId: z.uuid(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type ListMonitoringHoursEventsQuery = z.infer<typeof listMonitoringHoursEventsQuery>;

export const createMonitoringHoursEventBody = z.object({
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  patientId: z.uuid(),
  ruleId: z.uuid(),
});
export type CreateMonitoringHoursEventBody = z.infer<typeof createMonitoringHoursEventBody>;

export const updateMonitoringHoursEventBody = z.object({
  start: z.iso.datetime({ offset: true }).optional(),
  end: z.iso.datetime({ offset: true }).optional(),
  ruleId: z.uuid().optional(),
});
export type UpdateMonitoringHoursEventBody = z.infer<typeof updateMonitoringHoursEventBody>;
